import axios from 'axios'
import { t } from '../../../shared/config/translations'
import {
	SUPPORT_SUBMISSION_KINDS,
	type SupportSubmissionKind,
} from '../../../shared/types/support'
import { getSessionSignal } from '../../api/requestSession'
import { submitSupportSubmission } from '../../api/support'
import { renderSpinner } from '../../components/Loader'
import {
	closeOverlay,
	type OverlayView,
	pushOverlayContent,
} from '../../components/Overlay'
import { showSnackbar } from '../../components/Snackbar'
import { collectBugReportContext } from '../../utils/bugReportContext'
import { settingsOverlayPath } from './settingsOverlayRoutes'
import {
	type SettingsHelpOverlayKey,
	settingsHelpOverlayTemplate,
} from './settingsPageTemplates'

/** Opens the selected settings help/support overlay. */
export function openSettingsSupportOverlay(
	settingsOverlayKey: SettingsHelpOverlayKey,
): void {
	pushOverlayContent(settingsSupportOverlay(settingsOverlayKey))
}

/** Renders the selected settings help/support overlay. */
function settingsSupportOverlay(
	settingsOverlayKey: SettingsHelpOverlayKey,
): OverlayView {
	return {
		path: settingsOverlayPath(settingsOverlayKey),
		html: settingsHelpOverlayTemplate(settingsOverlayKey),
		onMount: bindOverlayForm,
		options: { link: false },
	}
}

/** Binds the support overlay form submit handler. */
function bindOverlayForm(): void {
	const formEl = document.querySelector<HTMLFormElement>(
		'[data-settings-overlay-form]',
	)
	formEl?.addEventListener('submit', handleOverlayFormSubmit)
	const copyInput =
		formEl?.querySelector<HTMLInputElement>('[name="sendCopy"]')
	const emailInput = formEl?.querySelector<HTMLInputElement>('[name="email"]')
	const updateEmailRequirement = () => {
		if (!emailInput) return
		emailInput.required = copyInput?.checked ?? false
	}
	copyInput?.addEventListener('change', updateEmailRequirement)
	updateEmailRequirement()
}

/** Validates and submits the support overlay form. */
async function handleOverlayFormSubmit(event: SubmitEvent): Promise<void> {
	event.preventDefault()

	const formEl = event.currentTarget
	if (!(formEl instanceof HTMLFormElement)) return
	if (!formEl.reportValidity()) return

	const supportKind = formEl.dataset.supportKind
	const isSupportSubmissionKind = SUPPORT_SUBMISSION_KINDS.includes(
		supportKind as SupportSubmissionKind,
	)
	if (!isSupportSubmissionKind) return

	const submitButtonEl = formEl.querySelector<HTMLButtonElement>(
		'button[type="submit"]',
	)
	const spinnerEl =
		submitButtonEl?.querySelector<HTMLDivElement>('.spinner') ??
		(submitButtonEl ? renderSpinner(submitButtonEl) : null)
	const formData = new FormData(formEl)
	const message = String(formData.get('message') ?? '').trim()
	const email = String(formData.get('email') ?? '').trim()
	const sessionSignal = getSessionSignal()

	if (!message) return

	submitButtonEl?.setAttribute('disabled', 'true')
	spinnerEl?.classList.add('visible')

	try {
		const result = await submitSupportSubmission({
			context:
				supportKind === 'report-bug'
					? await collectBugReportContext()
					: undefined,
			sendCopy: formData.has('sendCopy'),
			email: email || undefined,
			kind: supportKind as SupportSubmissionKind,
			message,
		})
		if (sessionSignal.aborted) return
		showSnackbar(
			t(
				result.data.copyStatus === 'failed'
					? 'settings.supportCopyFailed'
					: 'settings.supportSubmitSuccess',
			),
			result.data.copyStatus === 'failed' ? 'error' : 'default',
		)
		closeOverlay()
	} catch (err) {
		if (sessionSignal.aborted) return
		console.error('Support submission failed', err)
		showSnackbar(
			t(
				axios.isAxiosError(err) && err.response?.status === 429
					? 'settings.supportRateLimited'
					: 'settings.supportSubmitError',
			),
			'error',
		)
	} finally {
		submitButtonEl?.removeAttribute('disabled')
		spinnerEl?.classList.remove('visible')
	}
}
